import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Save, Loader2, AlertTriangle, Users } from 'lucide-react';
import {
  updateContractPlans,
  ContractDetails,
  ContractPlanDetail,
  ContractPlanInput,
  OverflowPlan,
  ActiveUserOnPlanSummary,
} from '@/services/contractApi';
import { PlanTier } from '@/services/organizationApi';

interface Props {
  enterpriseId: string;
  contract: ContractDetails;
  availablePlans: PlanTier[];
  onReload: () => void | Promise<void>;
  /**
   * Notifie le parent quand le total de sièges prévu (somme des rows en cours
   * d'édition) change. Permet d'alimenter en live la card "Utilisation des sièges"
   * sans devoir lift le state des rows hors du composant.
   */
  onTotalSeatsChange?: (totalSeats: number) => void;
}

interface PlanRow {
  planSlug: string;
  teamQuantity: number;
  // Snapshot du nombre d'users actuellement actifs sur ce plan (pour
  // détecter le downgrade côté UI avant l'aller-retour serveur).
  currentUsed: number;
  isExisting: boolean;
}

interface DowngradeOverflowState {
  overflowPlans: OverflowPlan[];
  removedPlan?: { planTierId: string; activeUsers: ActiveUserOnPlanSummary[] };
  /** Set des user IDs sélectionnés pour désactivation, indexé par planSlug. */
  selectedToDeactivate: Set<string>;
}

function extractErrorDetails(err: unknown): {
  status: number;
  message: string;
  code?: string;
  overflowPlans?: OverflowPlan[];
  removedPlanId?: string;
  activeUsers?: ActiveUserOnPlanSummary[];
} {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { status?: number; data?: { error?: string; details?: Record<string, unknown> } } }).response;
    const details = response?.data?.details as {
      code?: string;
      overflowPlans?: OverflowPlan[];
      removedPlanId?: string;
      activeUsers?: ActiveUserOnPlanSummary[];
    } | undefined;
    return {
      status: response?.status ?? 0,
      message: response?.data?.error ?? '',
      code: details?.code,
      overflowPlans: details?.overflowPlans,
      removedPlanId: details?.removedPlanId,
      activeUsers: details?.activeUsers,
    };
  }
  return { status: 0, message: 'Erreur inconnue' };
}

/**
 * Convertit le tableau ContractPlanDetail (vue backend) en rangées éditables.
 */
function toRows(details: ContractPlanDetail[]): PlanRow[] {
  return details.map((d) => ({
    planSlug: d.planSlug ?? '',
    teamQuantity: d.seatsPurchased,
    currentUsed: d.seatsUsed,
    isExisting: true,
  }));
}

export function ContractPlansEditor({ enterpriseId, contract, availablePlans, onReload, onTotalSeatsChange }: Props) {
  const [rows, setRows] = useState<PlanRow[]>(() => toRows(contract.plans));
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [overflow, setOverflow] = useState<DowngradeOverflowState | null>(null);

  // Plans non Solo, non déjà choisis dans le tableau actuel
  const usableSlugs = new Set(rows.map((r) => r.planSlug).filter(Boolean));
  const addablePlans = availablePlans.filter((p) => p.slug !== 'solo' && !usableSlugs.has(p.slug));

  // Total prévu = somme des sièges en cours d'édition. Remonté au parent pour
  // alimenter la card "Utilisation des sièges" en live (preview avant save).
  const totalSeats = rows.reduce((sum, r) => sum + (r.teamQuantity || 0), 0);
  useEffect(() => {
    onTotalSeatsChange?.(totalSeats);
  }, [totalSeats, onTotalSeatsChange]);

  const isDirty =
    JSON.stringify(rows.map((r) => ({ s: r.planSlug, q: r.teamQuantity })).sort((a, b) => a.s.localeCompare(b.s)))
    !== JSON.stringify(contract.plans
      .map((p) => ({ s: p.planSlug ?? '', q: p.seatsPurchased }))
      .sort((a, b) => a.s.localeCompare(b.s)));

  const reasonOk = reason.trim().length >= 3;

  function minSeatsFor(slug: string): number {
    return availablePlans.find((p) => p.slug === slug)?.min_seats ?? 1;
  }
  // Quota par plan doit etre >= min_seats (cf migration 099). beta a min=1.
  const allRowsAboveMin = rows.every((r) => r.teamQuantity >= minSeatsFor(r.planSlug));

  const submittable = isDirty && reasonOk && rows.length > 0 && allRowsAboveMin && !saving;

  function addRow() {
    if (addablePlans.length === 0) {
      toast.error('Tous les plans disponibles sont déjà dans le mix.');
      return;
    }
    // Ordre de préférence : lead, solve, detect, beta. Fallback : premier addable.
    const preferenceOrder = ['lead', 'solve', 'detect', 'beta'];
    const next =
      preferenceOrder
        .map((slug) => addablePlans.find((p) => p.slug === slug))
        .find((p) => p !== undefined)
      ?? addablePlans[0];
    setRows([...rows, { planSlug: next.slug, teamQuantity: next.min_seats ?? 5, currentUsed: 0, isExisting: false }]);
  }

  function removeRow(idx: number) {
    setRows(rows.filter((_, i) => i !== idx));
  }

  function updateRow(idx: number, patch: Partial<PlanRow>) {
    setRows(rows.map((r, i) => {
      if (i !== idx) return r;
      const next = { ...r, ...patch };
      // Si on change le slug, clamper la quantité au min du nouveau plan
      // pour ne pas rester sous min_seats apres switch.
      if (patch.planSlug && patch.planSlug !== r.planSlug) {
        const min = minSeatsFor(patch.planSlug);
        if (next.teamQuantity < min) next.teamQuantity = min;
      }
      return next;
    }));
  }

  function buildPayloadPlans(): ContractPlanInput[] {
    return rows.map((r) => ({ planSlug: r.planSlug, teamQuantity: r.teamQuantity }));
  }

  async function submit(usersToDeactivate?: string[]) {
    if (!reasonOk) {
      toast.error('Raison requise (3 caractères minimum)');
      return;
    }
    setSaving(true);
    try {
      await updateContractPlans(enterpriseId, {
        plans: buildPayloadPlans(),
        reason,
        ...(usersToDeactivate && usersToDeactivate.length > 0 ? { usersToDeactivate } : {}),
      });
      toast.success('Plans du contrat mis à jour');
      setOverflow(null);
      setReason('');
      await onReload();
    } catch (err) {
      const details = extractErrorDetails(err);
      if (details.code === 'PRE_DOWNGRADE_DEACTIVATION_REQUIRED') {
        // Ouvrir la modale de résolution
        setOverflow({
          overflowPlans: details.overflowPlans ?? [],
          removedPlan: details.removedPlanId
            ? { planTierId: details.removedPlanId, activeUsers: details.activeUsers ?? [] }
            : undefined,
          selectedToDeactivate: new Set<string>(),
        });
      } else {
        toast.error(`Échec : ${details.message || 'Erreur inconnue'}`);
      }
    } finally {
      setSaving(false);
    }
  }

  function toggleSelection(userId: string) {
    if (!overflow) return;
    const next = new Set(overflow.selectedToDeactivate);
    if (next.has(userId)) next.delete(userId);
    else next.add(userId);
    setOverflow({ ...overflow, selectedToDeactivate: next });
  }

  async function confirmDeactivation() {
    if (!overflow) return;
    const usersToDeactivate = Array.from(overflow.selectedToDeactivate);
    if (usersToDeactivate.length === 0) {
      toast.error('Sélectionnez au moins un utilisateur à désactiver.');
      return;
    }
    await submit(usersToDeactivate);
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h2 className="text-sm font-semibold text-gray-900 mb-4">
        Plans du contrat (multi-plan)
      </h2>

      <div className="space-y-2 mb-4">
        {rows.length === 0 && (
          <p className="text-sm text-gray-500 py-3 italic">Aucun plan dans le mix.</p>
        )}
        {rows.map((row, idx) => {
          const planTier = availablePlans.find((p) => p.slug === row.planSlug);
          const wouldOverflow = row.currentUsed > row.teamQuantity;
          const rowMin = minSeatsFor(row.planSlug);
          const belowMin = row.teamQuantity < rowMin;
          // belowMin a priorite visuelle sur wouldOverflow (rouge > ambre).
          const borderClass = belowMin
            ? 'border-rose-300 bg-rose-50'
            : wouldOverflow ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-gray-50';
          return (
            <div
              key={`${row.planSlug}-${idx}`}
              className={`flex items-center gap-3 p-3 rounded-lg border ${borderClass}`}
            >
              <select
                value={row.planSlug}
                onChange={(e) => updateRow(idx, { planSlug: e.target.value })}
                disabled={row.isExisting}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white disabled:bg-gray-100"
              >
                {planTier && <option value={planTier.slug}>{planTier.name}</option>}
                {!row.isExisting && availablePlans
                  .filter((p) => p.slug !== 'solo' && (p.slug === row.planSlug || !usableSlugs.has(p.slug)))
                  .map((p) => (
                    <option key={p.id} value={p.slug}>{p.name}</option>
                  ))}
              </select>
              <input
                type="number"
                min={rowMin}
                value={row.teamQuantity}
                onChange={(e) => updateRow(idx, { teamQuantity: parseInt(e.target.value, 10) || rowMin })}
                className={`w-24 px-3 py-2 border rounded-lg text-sm ${
                  belowMin ? 'border-rose-400 bg-white' : 'border-gray-300'
                }`}
                aria-invalid={belowMin}
                title={`Minimum ${rowMin} siège${rowMin > 1 ? 's' : ''} pour ce plan`}
              />
              <span className="text-xs text-gray-500 w-28 text-right">
                <Users className="w-3 h-3 inline mr-1" />
                {row.currentUsed} actif{row.currentUsed > 1 ? 's' : ''}
                {belowMin && (
                  <span className="block text-rose-600 font-medium">min {rowMin}</span>
                )}
              </span>
              <button
                onClick={() => removeRow(idx)}
                className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg"
                title="Retirer ce plan du mix"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>

      <button
        onClick={addRow}
        disabled={addablePlans.length === 0}
        className="flex items-center gap-2 px-3 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg disabled:text-gray-400 disabled:hover:bg-transparent"
      >
        <Plus className="w-4 h-4" />
        Ajouter un plan au mix
      </button>

      <div className="mt-4 pt-4 border-t border-gray-100">
        <label className="block text-xs font-medium text-gray-700 mb-1">Raison (audit) *</label>
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="ex. Avenant 2026-05-15 — passage Solve 30 → 25 + ajout Lead 8"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
        />
        <p className="mt-1 text-xs text-gray-500">
          Tout changement de mix est tracé dans contract_audit_events (qui, quand, avant → après, raison).
        </p>
      </div>

      <div className="flex justify-end mt-4">
        <button
          onClick={() => submit()}
          disabled={!submittable}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Enregistrer le mix
        </button>
      </div>

      {/* Downgrade resolution modale */}
      {overflow && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="downgrade-modale-title"
          aria-describedby="downgrade-modale-description"
        >
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <h3 id="downgrade-modale-title" className="text-lg font-semibold text-gray-900">
                  Désactivation requise avant downgrade
                </h3>
                <p id="downgrade-modale-description" className="text-sm text-gray-600 mt-1">
                  Le quota cible est inférieur au nombre d'utilisateurs actifs sur certains plans.
                  Sélectionnez les utilisateurs à désactiver, puis confirmez le downgrade.
                </p>
              </div>
            </div>

            {overflow.overflowPlans.map((op) => {
              const excess = op.activeUsers.length - op.requestedQuota;
              return (
                <div key={op.planTierId} className="mb-4 p-3 bg-amber-50 rounded-lg">
                  <div className="text-sm font-medium text-amber-800 mb-2">
                    Plan {op.planName} — {op.activeUsers.length} actifs, quota cible {op.requestedQuota}
                    <span className="ml-2 text-xs">({excess} à désactiver)</span>
                  </div>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {op.activeUsers.map((u) => (
                      <label key={u.id} className="flex items-center gap-2 text-sm py-1 px-2 hover:bg-amber-100 rounded">
                        <input
                          type="checkbox"
                          checked={overflow.selectedToDeactivate.has(u.id)}
                          onChange={() => toggleSelection(u.id)}
                        />
                        <span className="font-medium">{u.firstName} {u.lastName}</span>
                        <span className="text-gray-500">{u.email}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}

            {overflow.removedPlan && (
              <div className="mb-4 p-3 bg-rose-50 rounded-lg">
                <div className="text-sm font-medium text-rose-800 mb-2">
                  Plan supprimé du mix — tous les utilisateurs doivent être désactivés
                </div>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {overflow.removedPlan.activeUsers.map((u) => (
                    <label key={u.id} className="flex items-center gap-2 text-sm py-1 px-2 hover:bg-rose-100 rounded">
                      <input
                        type="checkbox"
                        checked={overflow.selectedToDeactivate.has(u.id)}
                        onChange={() => toggleSelection(u.id)}
                      />
                      <span className="font-medium">{u.firstName} {u.lastName}</span>
                      <span className="text-gray-500">{u.email}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setOverflow(null)}
                className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Annuler
              </button>
              <button
                onClick={confirmDeactivation}
                disabled={saving}
                className="flex items-center gap-2 px-3 py-1.5 bg-rose-600 text-white text-sm rounded-lg hover:bg-rose-700 disabled:bg-gray-300"
              >
                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Désactiver les sélectionnés et appliquer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
